// Costes de flota: tipos, llamadas al API (/fleet-costs) y lo puro de la
// pantalla (filas del Excel, variación contra el periodo anterior).
import api from "../../utils/api";
import { getJson } from "../../lib/http";

export const CATEGORIES = ["compra", "servicio", "mantenimiento", "combustible", "equipamiento", "otro"] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Record<Category, string> = {
  compra: "Compra",
  servicio: "Servicio",
  mantenimiento: "Mantenimiento",
  combustible: "Combustible",
  equipamiento: "Equipamiento",
  otro: "Otro",
};

export type Recurrence = "month" | "year" | null;

export interface FleetCost {
  id: string;
  vehicle_id: string;
  category: Category;
  amount: number;
  date: string;
  occurrence: string; // fecha de esta aparición (un recurrente aparece varias veces)
  concept?: string;
  note?: string;
  recurrence?: Recurrence;
}

export interface CostSummary {
  total: number;
  perDay: number;
  count: number;
  days: number;
  byCategory: Partial<Record<Category, number>>;
  byVehicle: Record<string, number>;
  byMonth: Record<string, number>;
  // Sólo en el periodo actual. null: rango demasiado grande o sin resúmenes diarios.
  usage?: { km: number; engineHours: number } | null;
  perKm?: number | null;
  perHour?: number | null;
}

export interface FleetCostsResponse {
  items: FleetCost[];
  summary: CostSummary;
  previous: CostSummary;
}

export type CostInput = Pick<FleetCost, "vehicle_id" | "category" | "amount" | "date"> & {
  concept?: string;
  note?: string;
  recurrence?: Recurrence;
};

const URL = "/fleet-costs";
const headers = (companyId: string) => ({ "x-company-id": companyId });

export const fetchFleetCosts = async (companyId: string, from: string, to: string, vehicleId?: string | null) => {
  const res = await getJson<{ data: { data: FleetCostsResponse } }>(
    URL,
    { from, to, ...(vehicleId ? { vehicle_id: vehicleId } : {}) },
    undefined,
    { headers: headers(companyId) },
  );
  return res.data.data;
};

export const createFleetCost = (companyId: string, input: CostInput) => api.post(URL, input, { headers: headers(companyId) });
export const updateFleetCost = (companyId: string, id: string, input: Partial<CostInput>) =>
  api.put(URL, input, { headers: headers(companyId), params: { id } });
export const deleteFleetCost = (companyId: string, id: string) => api.delete(URL, { headers: headers(companyId), params: { id } });
export const importFleetCosts = (companyId: string, rows: ImportRow[]) => api.post(`${URL}/import`, { rows }, { headers: headers(companyId) });

/** Mensaje del API (400/403/404) o uno genérico. */
export const apiMessage = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { data?: { message?: string } } } })?.response?.data?.data?.message || fallback;

/** Errores por fila que devuelve la importación cuando no guardó nada. */
export const importErrors = (e: unknown): { row: number; errors: string[] }[] =>
  (e as { response?: { data?: { data?: { data?: { errors?: { row: number; errors: string[] }[] } } } } })?.response?.data?.data?.data?.errors ?? [];

/** Variación contra el periodo anterior, como Mapon («+384 %»). null si antes era 0. */
export const pctChange = (current: number, previous: number): number | null =>
  previous > 0 ? ((current - previous) / previous) * 100 : null;

// ---------------------------------------------------------------------------
// Importación desde Excel. Encabezados de la plantilla (sin importar acentos,
// mayúsculas ni el orden de las columnas).
// ---------------------------------------------------------------------------
export const TEMPLATE_HEADERS = ["Unidad", "Categoría", "Concepto", "Monto", "Fecha", "Recurrente", "Nota"];

export interface ImportRow {
  vehicle: string;
  category: string;
  concept?: string;
  amount: number | string;
  date: string;
  recurrence?: Recurrence;
  note?: string;
}

const norm = (s: unknown) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const HEADER_KEYS: Record<string, keyof ImportRow> = {
  unidad: "vehicle",
  placa: "vehicle",
  vehiculo: "vehicle",
  categoria: "category",
  concepto: "concept",
  monto: "amount",
  costo: "amount",
  importe: "amount",
  fecha: "date",
  recurrente: "recurrence",
  nota: "note",
};

const RECURRENCE_WORDS: Record<string, Recurrence> = { mensual: "month", mes: "month", anual: "year", ano: "year", "": null, no: null };

/** dd/mm/aaaa (como lo escribe un usuario en México), ISO o Date de Excel. */
const toIsoDate = (v: unknown): string => {
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString();
  const s = String(v ?? "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 12)).toISOString();
  return s; // ISO o algo inválido: lo valida el API y dice qué fila
};

/**
 * Convierte las filas de la hoja (la primera es de encabezados) en lo que
 * espera POST /fleet-costs/import. Las filas totalmente vacías se ignoran.
 */
export const rowsFromSheet = (sheet: unknown[][]): ImportRow[] => {
  if (sheet.length < 2) return [];
  const cols = sheet[0].map((h) => HEADER_KEYS[norm(h)]);
  return sheet
    .slice(1)
    .filter((r) => r.some((c) => String(c ?? "").trim() !== ""))
    .map((r) => {
      const out: Partial<ImportRow> = {};
      cols.forEach((key, i) => {
        if (!key) return;
        const v = r[i];
        if (key === "date") out.date = toIsoDate(v);
        // `in` y no `??`: «no recurrente» se guarda como null y `??` lo saltaría.
        else if (key === "recurrence") out.recurrence = norm(v) in RECURRENCE_WORDS ? RECURRENCE_WORDS[norm(v)] : (norm(v) as Recurrence);
        else if (key === "amount") out.amount = typeof v === "number" ? v : String(v ?? "").replace(/[$,\s]/g, "");
        else if (key === "category") out.category = norm(v);
        else (out as Record<string, unknown>)[key] = String(v ?? "").trim();
      });
      return out as ImportRow;
    });
};
